import {
  SKILL_TYPE_BOOL,
  SKILL_TYPE_ENUM,
  SKILL_TYPE_INT,
  SKILL_TYPE_STRING,
} from "./CustomSkillsTypes";

/**
 * One allowed value of an enum skill, as the API may send it.
 *
 * Apollo has not shipped this yet. It can reasonably come back as a bare string list or as a
 * list of rows with an id, so the model accepts both and normalises to the only thing the UI
 * and TaskRouter care about: the ordered list of spellings. Anything else (a number, a null,
 * a row with no `value`) is dropped rather than rendered.
 */
export interface ICustomSkillValue {
  id?: number
  value: string
}

export interface ICustomSkill{
  id:number
  type_id: number
  key:string
  /** Present (and non-empty) only for enum skills. Absent on every other type. */
  values?: Array<string | ICustomSkillValue> | null
}

/** Order is preserved; blanks, duplicates and malformed entries are discarded. */
export function NormalizeSkillValues(raw: Array<string | ICustomSkillValue> | null | undefined): string[] {
  if (!Array.isArray(raw)) {
    return []
  }
  const normalized: string[] = []
  raw.forEach((item) => {
    let value = ''
    if (typeof item === 'string') {
      value = item
    } else if (item !== null && typeof item === 'object' && typeof item.value === 'string') {
      value = item.value
    } else {
      return
    }
    if (value.length > 0 && normalized.indexOf(value) < 0) {
      normalized.push(value)
    }
  })
  return normalized
}

export class CustomSkill {
    private id: number = 0;
    public get Id() : number {
      return this.id
    }

    public set Id(v : number) {
      this.id = v
    }

    private key: string = ''
    public get Key() : string {
      return this.key
    }
    public set Key(v : string) {
      this.key = v
    }

    private type_id: number = -1
    public get TypeId() : number {
      return this.type_id
    }
    public set TypeId(v : number) {
      this.type_id = v;
    }

    /**
     * Allowed values of an enum skill, in the order the operator put them in.
     *
     * This field is serialised by `JSON.stringify` on POST /skills, which is how the add-skill
     * modal ships an enum's vocabulary. It is always an array so the payload shape does not
     * change between types - empty for everything that is not an enum.
     */
    private values: string[] = []
    public get Values() : string[] {
      return this.values
    }
    public set Values(v : string[]) {
      this.values = v
    }

    private _created: number = 0
    public get Created(){
      return this._created
    }

    /**
     * Why a half-typed value may go into state. Deliberately looser than ValueError: "-" and
     * "1." are not numbers but a user on the way to "-1.5" has to be able to type them.
     * Returns false for a keystroke the type cannot ever contain, so the input simply refuses
     * the character instead of storing something the save will later reject.
     */
    public AcceptsInput(value: string): boolean {
      switch (this.type_id) {
        case SKILL_TYPE_INT:
          return /^-?\d*(\.\d*)?$/.test(value)
        case SKILL_TYPE_BOOL:
          return value === '' || value === '0' || value === '1'
        case SKILL_TYPE_ENUM:
          return value === '' || this.values.indexOf(value) >= 0
        case SKILL_TYPE_STRING:
          // Anything: the server stores any non-empty text, and a gate that eats
          // the space key makes a legal value untypeable.
          return true
        default:
          // Unrecognised type: this build does not know the rule, so it must not invent one.
          // The widget shows a permanent warning instead of silently dropping keystrokes.
          return true
      }
    }

    /**
     * '' when the value is storable, otherwise the reason, phrased for an operator.
     *
     * This is the single source of truth for "is this skill value valid" - the row widget uses
     * it to mark itself red and the Apply handler uses it to refuse the save, so the two can
     * never disagree.
     */
    public ValueError(value: string): string {
      switch (this.type_id) {
        case SKILL_TYPE_STRING:
          if (value.length === 0) {
            return 'value is required'
          }
          // Non-empty is the WHOLE rule, because it is the server's whole rule. A
          // stricter client would refuse to save an agent over a value the system
          // already holds and considers fine — the box would be red on data nobody
          // can edit away without losing it.
          if (value.trim().length === 0) {
            return 'value is required'
          }
          return ''
        case SKILL_TYPE_INT:
          if (value.length === 0) {
            return 'value is required'
          }
          // Exactly the server's regex. `007` passes a looser one here and is then
          // refused with a 409 — a round trip to learn what this box already knew.
          if (!/^-?(?:0|[1-9][0-9]*)(?:\.[0-9]+)?$/.test(value)) {
            return 'must be a number'
          }
          return ''
        case SKILL_TYPE_BOOL:
          if (value !== '0' && value !== '1') {
            return 'must be ticked or unticked - open the agent and set it'
          }
          return ''
        case SKILL_TYPE_ENUM:
          if (this.values.length === 0) {
            return 'this enum skill has no allowed values; it cannot be used until they are declared'
          }
          if (this.values.indexOf(value) < 0) {
            return 'must be one of: ' + this.values.join(', ')
          }
          return ''
        default:
          if (value.length === 0) {
            return 'value is required'
          }
          return ''
      }
    }

    constructor(o: ICustomSkill | undefined | 'undefined' = undefined){
      if(o !== undefined && o !== 'undefined'){
      this.id = o.id
      this.type_id = o.type_id
      this.key = o.key
      this.values = NormalizeSkillValues(o.values)
      }
      this._created = (new Date()).getTime();
    }
  }
